// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

function FullPageSpinner() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="h-8 w-8 motion-safe:animate-spin rounded-full border-b-2 border-primary" />
    </div>
  );
}

/**
 * Gate for every non-install route. Resolves in order:
 *  1. Org not yet installed → /install (the wizard is the one pre-IdP
 *     unauthenticated surface; this must win before any login redirect so a
 *     fresh install isn't bounced to an IdP that doesn't exist yet).
 *  2. Installed but no session → the rendered /login page (docs/10 §Login
 *     flow), the same page the gateway's content-negotiated 302 targets — so a
 *     401 caught client-side and one caught at the gateway land on the same
 *     surface. Its "Sign in" button starts the OIDC dance via /auth/login.
 *     Gated on a *confirmed* 401 (`unauthenticated`), not merely a null user: a
 *     transient /api/me failure must not bounce (and loop) on a login that
 *     can't fix it.
 *  3. Installed and authenticated → render the protected route. A null user
 *     from a non-401 identity error also falls through to render (degraded, with
 *     an empty shell) rather than redirecting.
 *
 * Renders a spinner while either the install-status or the identity check is in
 * flight, to avoid a flash of protected content or a premature redirect.
 */
export function InstallGuard() {
  const { loading: authLoading, unauthenticated } = useAuth();
  const { data, isLoading } = api.install.getStatus.useQuery(['install-status'], {});
  const location = useLocation();

  const body = data?.status === 200 ? data.body : null;
  // If we can't reach the install endpoint (e.g. no org yet) or installedAt is
  // null, the org isn't installed.
  const installed = Boolean(body && body.installedAt !== null);
  // The wizard's Finish buttons arrive with this flag: the status it cached is
  // from before the install was recorded, so it is refreshed instead of trusted.
  const justInstalled = Boolean(
    (location.state as { justInstalled?: boolean } | null)?.justInstalled,
  );
  const queryClient = useQueryClient();
  React.useEffect(() => {
    if (justInstalled) void queryClient.invalidateQueries({ queryKey: ['install-status'] });
  }, [justInstalled, queryClient]);
  const needsLogin = !isLoading && installed && !authLoading && unauthenticated;

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (!installed && !justInstalled) {
    return <Navigate to="/install" replace />;
  }

  if (needsLogin) {
    const here = location.pathname + location.search;
    return <Navigate to={`/login?return_to=${encodeURIComponent(here)}`} replace />;
  }

  // Identity still resolving.
  if (authLoading) {
    return <FullPageSpinner />;
  }

  return <Outlet />;
}
